import { defineStore } from 'pinia';
import { toRaw } from 'vue';
import API from '@/api';
import { MerchantUserRole } from '@/api/merchants';
import { PlatformUserRole } from '@/api/narrative';
import { ServiceProviderUserRole } from '@/api/serviceProviders';
import { userInterface, userOnboardingInterface } from '@/api/users';
import { AppEntitlementEnum, AppPermissionEnum } from '@/compiler/types';
import { getLoginUrlWithCurrentPageRedirect } from '@/helpers/getLoginUrlWithCurrentPageRedirect';
import { setCurrentUser } from '@/state/authState';

const partnerAppRoles: PlatformUserRole[] = ['partner', 'developer'];

let loadCurrentUserPromise: Promise<void> | null = null;
let loadPermissionsPromise: Promise<void> | null = null;
let loadEntitlementsPromise: Promise<void> | null = null;
let loadUserRolesPromise: Promise<void> | null = null;

interface State {
    user?: userInterface
    permissions?: Record<AppPermissionEnum, boolean>
    entitlements?: AppEntitlementEnum[]
    userRoles?: {
        serviceProvider: ServiceProviderUserRole[]
        merchant: MerchantUserRole[]
        platform: PlatformUserRole[]
    }
}

export const useCurrentUserStore = defineStore('currentUser', {
    state: (): State => ({
        user: undefined,
        permissions: undefined,
        userRoles: undefined,
        entitlements: undefined,
    }),
    getters: {
        currentUser: state => state.user,
    },
    actions: {
        async loadCurrentUser(redirectWhenUnauthenticated = false): Promise<void> {
            if (this.user) {
                return;
            }

            if (loadCurrentUserPromise) {
                return loadCurrentUserPromise;
            }

            loadCurrentUserPromise = this.refreshCurrentUser(redirectWhenUnauthenticated).finally(() => {
                loadCurrentUserPromise = null;
            });

            return loadCurrentUserPromise;
        },
        async refreshCurrentUser(redirectWhenUnauthenticated = false): Promise<void> {
            const user = await API.users.fetchCurrentUser();

            // When unauthenticated, /api/users/current returns 200 with an empty array `[]`
            // (an authenticated response is `{ data: {...} }`), so `fetchCurrentUser`
            // resolves without a user instead of the interceptor's 401 session-expiry path.
            if (!user) {
                // Only the authenticated layout, which throws "User is required" from
                // useNarrative's currentUser getter if it renders without a user, asks us
                // to redirect (the session-race case from PREPBUSINESS-B1H). Guest pages
                // are unauthenticated by design and the router guards resolve the no-user
                // case themselves, so redirecting for them would bounce the login page to
                // itself and reload forever. For those callers, resolve with no user.
                if (redirectWhenUnauthenticated) {
                    window.location.href = getLoginUrlWithCurrentPageRedirect();

                    // Never resolve, so callers awaiting the global stores never flip the
                    // layout into its rendered state before the redirect takes effect.
                    return new Promise<void>(() => {});
                }

                this.user = undefined;
                setCurrentUser(null);

                return;
            }

            this.user = user;
            setCurrentUser(user);
        },
        async loadPermissions(): Promise<void> {
            if (this.permissions) {
                return;
            }

            if (loadPermissionsPromise) {
                return loadPermissionsPromise;
            }

            loadPermissionsPromise = this.refreshPermissions().finally(() => {
                loadPermissionsPromise = null;
            });

            return loadPermissionsPromise;
        },
        async refreshPermissions(): Promise<void> {
            this.permissions = await API.narrative.fetchCurrentPermissions();
        },
        async loadEntitlements(): Promise<void> {
            if (this.entitlements) {
                return;
            }

            if (loadEntitlementsPromise) {
                return loadEntitlementsPromise;
            }

            loadEntitlementsPromise = API.narrative.fetchCurrentFeatures().then((entitlements) => {
                this.entitlements = entitlements;
            }).finally(() => {
                loadEntitlementsPromise = null;
            });

            return loadEntitlementsPromise;
        },
        async loadUserRoles(): Promise<void> {
            if (this.userRoles) {
                return;
            }

            if (loadUserRolesPromise) {
                return loadUserRolesPromise;
            }

            loadUserRolesPromise = this.refreshUserRoles().finally(() => {
                loadUserRolesPromise = null;
            });

            return loadUserRolesPromise;
        },
        async refreshUserRoles(): Promise<void> {
            this.userRoles = await API.narrative.fetchCurrentUserRoles();
        },
        updateUserOnboarding(onboarding: userOnboardingInterface): Promise<void> {
            if (!this.user || JSON.stringify(toRaw(onboarding)) === JSON.stringify(toRaw(this.user.onboarding))) {
                return new Promise<void>(resolve => resolve());
            }

            this.user.onboarding = onboarding;

            return API.users.updateOnboarding(this.user.id, onboarding).then((onboardingResponse) => {
                if (!this.user) return;

                this.user.onboarding = onboardingResponse;
            });
        },
        can(permission: AppPermissionEnum): boolean {
            if (!this.permissions) return false;

            return this.permissions[permission];
        },
        hasEntitlement(entitlement: AppEntitlementEnum): boolean {
            if (!this.entitlements) return false;

            return this.entitlements.includes(entitlement);
        },
        userHasRoleOnCurrentServiceProvider(...roles: ServiceProviderUserRole[]): boolean {
            if (this.userRoles?.platform.includes('developer') === true) {
                return true;
            }

            if (!roles.length) {
                return Boolean(this.userRoles?.serviceProvider.length);
            }

            return Boolean(this.userRoles?.serviceProvider.some(role => roles.includes(role)));
        },
        userHasRoleOnCurrentMerchant(...roles: MerchantUserRole[]): boolean {
            if (this.userRoles?.platform.includes('developer') === true) {
                return true;
            }

            if (!roles.length) {
                return Boolean(this.userRoles?.merchant.length);
            }

            return Boolean(this.userRoles?.merchant.some(role => roles.includes(role)));
        },
        userIsDeveloper(): boolean {
            if (this.userRoles?.platform.includes('developer') === true) {
                return true;
            }

            return false;
        },
        userCanManagePartnerApps(): boolean {
            return this.userRoles?.platform.some(role => partnerAppRoles.includes(role)) === true;
        },
    },
});
